import axios, { RawAxiosRequestConfig, AxiosResponse, AxiosInstance } from "axios";
import { store } from "../../redux";
import { apiCallHeader } from "../types/api/header.types";

class abstractApiCaller {
  protected controller: AbortController;

  constructor() {
    this.controller = new AbortController();
  }

  public abortRequest(): boolean {
    this.controller.abort();
    return true;
  }

  protected async query<T>(
    apiCall: string,
    body: object = {},
    method: string = 'POST',
    callbackProgress?: (percentage: number) => void
  ) {

    const objHeader = this.getRequestHeader();
    const state = store.getState();

    const apiUrl = state.system.appConfig.apiUrl;

    const axiosClient = axios.create();
    axiosClient.defaults.baseURL = apiUrl;
    axiosClient.defaults.timeout = 19000;
    axiosClient.defaults.withCredentials = true;

    axios.interceptors.request.use(
      (request) => {
        request.headers.set('Access-Control-Allow-Origin', '*');
        request.headers.set('Access-Control-Allow-Methods', 'GET, POST, OPTIONS');
        request.headers.set('Access-Control-Allow-Headers', '*');
        request.headers.set('Content-Type', 'application/json');
        request.headers.set('Accept', 'application/json');
        return request;
      },
      (error) => {
        return error;
      },
      { synchronous: true }
    );

    axios.interceptors.response.use(
      (response) => {
        //Dispatch any action on success
        return response;
      },
      function (error) {
        if (error.response.status === 401) {
          //Add Logic to
          //1. Redirect to login page or
          //2. Request refresh token
        }
        return Promise.reject(error);
      }
    );

    let config: RawAxiosRequestConfig = {};

    if (typeof callbackProgress !== 'undefined') {
      config = {
        headers: objHeader,
      };
    } else {
      config = {
        headers: objHeader,
      };
    }

    const url =  apiUrl + apiCall;

    return this.getResponse(axiosClient, url, config, method);

  }

  protected async queryApi<T>(endpoint: string) {
    
    const state = store.getState();

    const apiToken = state.system.appConfig.apiKey;
    const apiUrl = state.system.appConfig.apiUrl;

    let objHeader = this.getRequestHeader();

    objHeader = {
      ...objHeader,
      'HeadersDefaults': 'common, delete, get, head',
      'Auth-Key' : apiToken
    };

    const config: RawAxiosRequestConfig = {
      headers: objHeader,
    };

    const axiosClient = axios.create({
      baseURL: apiUrl,
      timeout: 19000,
      withCredentials: true,
      headers: objHeader
    });

    const url =  apiUrl + endpoint;

    return this.get<T>(axiosClient, url, config);
  }

  protected getRequestHeader(): apiCallHeader {
    return {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, OPTIONS',
      'Access-Control-Allow-Headers': '*',
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
  }

  private async getResponse<T>(axiosClient: AxiosInstance, url: string, config: RawAxiosRequestConfig, method: string) {
    let response = '{}';
    try {
      switch (method) {
        case 'POST':
          return await this.post<T>(axiosClient, url, config);
        case 'PATCH':
          return await this.patch<T>(axiosClient, url, config);
        case 'PUT':
          return await this.put<T>(axiosClient, url, config);
        case 'DELETE':
          return await this.delete<T>(axiosClient, url, config);
        default:
          return await this.get<T>(axiosClient, url, config);
      }
    } catch (e) {
      console.error(e);
      return undefined as T;
    }
  }

  private get<T>(axiosClient: AxiosInstance, url: string, config: RawAxiosRequestConfig)
  {
    return axiosClient
    .get<T>(
      url,
      config
    )
    .then((response: AxiosResponse<T>) => {
      return response.data as T;
    })
    .catch((error) => {
      //console.error('api call error: ', error, error.message, apiCall, accessToken);
      return undefined as T;
    });
  }

  private post<T>(axiosClient: AxiosInstance, url: string, config: RawAxiosRequestConfig)
  {
    return axiosClient
    .post<T>(
      url,
      config
    )
    .then((response: AxiosResponse<T>) => {
      return response.data as T;
    })
    .catch((error) => {
      //console.error('api call error: ', error, error.message, apiCall, accessToken);
      return undefined as T;
    });
  }

  private patch<T>(axiosClient: AxiosInstance, url: string, config: RawAxiosRequestConfig)
  {
    return axiosClient
    .patch<T>(
      url,
      config
    )
    .then((response: AxiosResponse<T>) => {
      return response.data as T;
    })
    .catch((error) => {
      //console.error('api call error: ', error, error.message, apiCall, accessToken);
      return undefined as T;
    });
  }

  private put<T>(axiosClient: AxiosInstance, url: string, config: RawAxiosRequestConfig)
  {
    return axiosClient
    .put<T>(
      url,
      config
    )
    .then((response: AxiosResponse<T>) => {
      return response.data as T;
    })
    .catch((error) => {
      //console.error('api call error: ', error, error.message, apiCall, accessToken);
      return undefined as T;
    });
  }

  private delete<T>(axiosClient: AxiosInstance, url: string, config: RawAxiosRequestConfig)
  {
    return axiosClient
    .delete<T>(
      url,
      config
    )
    .then((response: AxiosResponse<T>) => {
      return response.data as T;
    })
    .catch((error) => {
      //console.error('api call error: ', error, error.message, apiCall, accessToken);
      return undefined as T;
    });
  }
}

export { abstractApiCaller };
